//import { raw } from 'body-parser';
import { duration } from 'moment';

/**
 * A utility class that attempts to retrieve data out of a provided SCORM
 * model/state.
 */
export class ScormStateUtils {

    /**
     * Returns whether or not the provided SCORM state is from a Chameleon course.
     * 
     * Even though the structures of SCORM states seem to be standardized, the
     * properties in each type of SCORM course may be used differently which can
     * suggest what type of course it is.
     * 
     * @param { object } model - The current state of the SCORM object.
     * @returns { boolean }
     */
    static isChameleon( model ) {
        // A Chameleon course's suspend_data appears to be a sequence of
        // two alphanumeric characters separated by spaces.
        // Example: 81 A4 64 61 74 61 95 82 ...
        const suspendData = model?.cmi?.suspend_data || '';
        const IS_CHAMELEON_SUSPEND_DATA_REGEX = /([\w]{2}\s?)+/g;
        const matches = suspendData.match( IS_CHAMELEON_SUSPEND_DATA_REGEX );
        if ( matches?.length !== 1 ) {
            return false;
        }

        // ...

        // If all above checks have passed, then we can conclude the data
        // probably comes from a Chameleon course.
        return true;
    }

    /**
     * Returns whether or not the provided SCORM state is from a Rise course.
     * 
     * Even though the structures of SCORM states seem to be standardized, the
     * properties in each type of SCORM course may be used differently which can
     * suggest what type of course it is.
     * 
     * @param { object } model - The current state of the SCORM object.
     * @returns { boolean }
     */
    static isRise( model ) {
        // A Rise course's suspend_data appears to be in JSON string.
        const suspendData = model?.cmi?.suspend_data || '';
        if ( suspendData.length <= 0 || suspendData[ 0 ] !== '{' ) {
            return false;
        }
        
        // A Rise course's lesson_location appears to be some URL path.
        const lessonLocation = model?.cmi?.core?.lesson_location || '';
        if ( !lessonLocation.includes( 'index.html#/' ) ) {
            return false;
        }

        // ...

        // If all above checks have passed, then we can conclude the data
        // probably comes from a Rise course.
        return true;
    }

    /**
     * Attempts to determine the type of the SCORM object by looking at its state.
     * 
     * @param { object } model - The current state of the SCORM object.
     * @returns { 'unknown' | 'chameleon' | 'rise' }
     */
    static getType( model ) {
        if ( this.isChameleon( model ) ) {
            return 'chameleon';
        }
        else if ( this.isRise( model ) ) {
            return 'rise';
        }
        else {
            return 'unknown';
        }
    }

    /**
     * Attempts to determine the status of the SCORM object by looking at its state.
     * 
     * @param { object } model - The current state of the SCORM object.
     * @returns { 'COMPLETED' | 'IN_PROGRESS' | 'ASSIGNED' }
     */
    static getStatus( model ) {
        const lessonStatus = model?.cmi?.core?.lesson_status || model?.cmi?.completion_status || '';
        const lessonMode = model?.cmi?.core?.lesson_mode || model?.cmi?.mode || '';
        
        if ( lessonMode === 'review' || lessonStatus === 'passed' || lessonStatus === 'completed' ) {
            return 'COMPLETED';
        }

        return 'IN_PROGRESS';
    }

    /**
     * Formats a duration value.
     * 
     * @param { number | string } value - The duration value to format (either as a string or number in milliseconds).
     * @param { 'string' | 'seconds' | 'milliseconds' } format - The format to return the time in.
     * @returns { string | number }
     */
    static formatDuration( value, format = 'string' ) {
        const moment = duration( value );
        switch ( format ) {
            case 'seconds': {
                return Math.floor( moment.asSeconds() );
            }

            case 'milliseconds': {
                return Math.floor( moment.asMilliseconds() );
            }

            case 'string':
            default: {
                let ms = moment.asMilliseconds();
                const hh = Math.floor( ms / ( 1000 * 60 * 60 ) );
                ms -= hh * 1000 * 60 * 60;
                const mm = Math.floor( ms / ( 1000 * 60 ) );
                ms -= mm * 1000 * 60;
                const ss = Math.floor( ms / 1000 );
                ms -= ss * 1000;
                const hhStr = `0000${ hh }`.substr( -4 );
                const mmStr = `00${ mm }`.substr( -2 );
                const ssStr = `00${ ss }`.substr( -2 );
                const msStr = ( ms / 1000 ).toFixed( 2 ).substr( -2 );
                return `${ hhStr }:${ mmStr }:${ ssStr }.${ msStr }`;
            }
        }
    }

    /**
     * Retrieves the user's total time spent in the course. This may not necessarily
     * include their current session time.
     * 
     * @param { object } model - The current state of the SCORM object.
     * @param { 'string' | 'seconds' | 'milliseconds' } format - The format to return the time in.
     * @returns { string }
     */
    static getTotalTime( model, format = 'string' ) {
        
        let sessionTime = model?.cmi?.core?.session_time;
        let totalTime = model?.cmi?.core?.total_time;
        
        if ( !sessionTime && !totalTime ) {
            sessionTime = model?.cmi?.session_time;
            totalTime = model?.cmi?.total_time;
        }
        
        if ( !sessionTime && !totalTime ) {
            return undefined;
        }

        if ( sessionTime.startsWith( 'P' ) ) {  
            const regex = /^P(?:\d+Y|Y)?(?:\d+M|M)?(?:\d+D|D)?(?:T(\d+H|H)?(\d+M|M)?(\d+(\.\d{1,2})?S|S)?)?$/;
            
            let matches;
            
            if ( ( matches = regex.exec( sessionTime ) ) !== null ) {
                let hours = 0, min = 0, sec = 0; 
                
                matches.forEach( ( match ) => {
                    if ( match ) {
                        // Set hours
                        if ( match.endsWith( 'H' ) ) {
                            hours = parseInt( match.slice( 0, -1 ) );
                            if ( isNaN( hours ) ) {
                                hours = 0;
                            }
                        }
                        
                        // Set minuetes
                        if ( match.endsWith( 'M' ) ) { 
                            min = parseInt( match.slice( 0, -1 ) );
                            if ( isNaN( min ) ) {
                                min = 0;
                            }
                        }
                        
                         // Set seconds
                        if ( match.endsWith( 'S' ) ) { 
                            sec = parseInt( match.slice( 0, -1 ) );
                            if ( isNaN( sec ) ) {
                                sec = 0;
                            }
                        }
                    }
                } );

                let time = ( hours * 3600 ) + ( min * 60 ) + sec;
                if ( time < 1 ) {
                  time = 1;
                }
 
                return time;
            }
        } else {
            let total = 0;

            if ( sessionTime ) {
                const time = Math.trunc( this.formatDuration( sessionTime, 'seconds' ) );
                total += time;
            }
            if ( totalTime ) {
                const time = Math.trunc( this.formatDuration( totalTime, 'seconds' ) );
                total += time;
            }
         
                    
            return total;    
        }
        
        return undefined;
    }

    /**
     * Retrieves the user's time spent in the course for their current session.
     * 
     * @param { object } model - The current state of the SCORM object.
     * @param { 'string' | 'seconds' | 'milliseconds' } format - The format to return the time in.
     * @returns { string }
     */
    static getSessionTime( model, format = 'string' ) {
        const timeStr = model?.cmi?.core?.session_time || model?.cmi?.session_time;
        if ( !timeStr ) {
            return undefined;
        }

        return this.formatDuration( timeStr, format );
    }   

    /**
     * Calculates the sum of the provided times.
     * 
     * @param { string[] } times - Strings representing a time (expected format: 0000:00:00.00).
     * @returns { string | number }
     */
    static sumOfTimes( times, format = 'string' ) {
        // Validate args.
        if ( !times || !Array.isArray( times ) || times.length <= 0 ) {
            return '0000:00:00.00';
        }

        let sum = duration( '0000:00:00.00' );
        for ( let i = 0; i < times.length; i++ ) {
            const durationVal = times[ i ];
            sum.add( duration( durationVal ) );
        }

        return this.formatDuration( sum, format );
    }

    /**
     * Converts the provided score value into a valid score value supported by the
     * Course Service. The output is clamped between 0 and 100. In `undefined` is
     * provided, `undefined` will be returned.
     * 
     * @param { string | number } value - The score value to format.
     * @returns { number }
     */
    static formatScoreValue( value ) {
        if ( value === undefined ) {
            return undefined;
        }

        const scoreNum = Math.round( parseFloat( value, 10 ) || 0 );
        const clampedScore = Math.min( Math.max( scoreNum, 0 ), 100 );
        return clampedScore;
    }

    /**
     * Attempts to determine the score of the SCORM object by looking at its state.
     * The score of a SCORM object might represent progress a user has made in the
     * course without necessarily being their tracked progress. For example, the
     * score might be the user's score obtained from a required quiz in the course.
     * 
     * @param { object } model - The current state of the SCORM object.
     * @returns { string | number }
     */
    static getUserQuizScore( model ) {
         
        if ( model.cmi.score?.scaled ) {
            const scaled =  model.cmi.score?.scaled * 100; 
            return parseInt( scaled, 10 );
        }
          
        const rawScore = model?.cmi?.core?.score?.raw || model?.cmi?.score?.raw || '';

        if ( rawScore ) { 
            return parseInt( rawScore, 10 );
        }
             
        return 0;
    }
}
